<template>
  <nav class="TOP-NAVIGATION">
    <template v-if="isPastStatusAlias">
      <span  class="text-md c-gray70" selenium="nav-num-jobs">{{ total.toFixed(0) }} jobs</span>
      <vk-listbox
          ref="listbox"
          class="FILTER"
          menuClass="MENU"
          multiple
          :value="filterStatusAliases"
          :display-text="filterStatusAliasesLabel"
          selenium="nav-all-archived-jobs"
          @input="onFilterInput"
          @focus.native="onFilterInteraction(true)"
          @blur.native="onFilterInteraction()">
        <vk-listbox-option
            v-for="{ value, label, count } in statusAliases"
            class="OPTION"
            :key="value"
            :value="value"
            :selenium="`archived-option-${ label.toLowerCase() }`">
          {{ label }} ({{ count }})
        </vk-listbox-option>
      </vk-listbox>
    </template>
    <span v-else-if="!isFulfilled" class="text-md c-gray70">
      Searching {{ filterQuery }} in all jobs...
    </span>
    <span v-else class="text-md c-gray70" selenium="search-results">
      {{ displayedTotal.toFixed(0) }} total
      result{{ displayedTotal !== 1 ? 's' : '' }}{{ filterQuery }} in all jobs
    </span>
  </nav>
</template>

<script>
import http from '@homeservices/api-kit/axios';

import jobParams from '@/mixins/job-params';
import jobs from '@/mixins/jobs';
import capitalize from '@/filters/capitalize';

export default {
  name: 'top-navigation',
  mixins: [
    jobParams,
    jobs,
  ],
  data () {
    const pastMatchStatuses = this.$options.PAST_MATCH_STATUSES
      .map(status => status);
    const matchStatusLabels = this.$options.MATCH_STATUS_LABELS;
    return {
      pastMatchStatuses,
      matchStatusLabels,
      statusAliases: pastMatchStatuses
        .map(status => ({
          value: status,
          label: capitalize(matchStatusLabels[status]),
          count: 0,
        })),
      isFilterOpened: false,
    };
  },
  computed: {
    filterQuery () {
      return this.customJobsQuery ? ` for "${this.customJobsQuery}"` : '';
    },
    filterStatusAliases () {
      return this.customJobsStatuses.length
        ? this.customJobsStatuses
        : this.pastMatchStatuses;
    },
    filterStatusAliasesLabel () {
      return this.filterStatusAliases.length < this.statusAliases.length
        ? `Chosen statuses (${this.filterStatusAliases.length})`
        : 'All Archived jobs';
    },
    isFetching () {
      return (
        this.meta.status === this.$options.REQUEST_LIFECYCLE.FETCHING
      );
    },
    isFulfilled () {
      return (
        this.meta.status === this.$options.REQUEST_LIFECYCLE.FULFILLED
      );
    },
    displayedTotal () {
      // Derive count from the same source as the job list
      // Add type guards and fallback logic
      const metaTotal = this.meta.total;
      const metaCount = this.meta.count;
      // Use meta.total if available, fallback to meta.count, then 0
      if (typeof metaTotal === 'number' && metaTotal >= 0) {
        return metaTotal;
      }
      if (typeof metaCount === 'number' && metaCount >= 0) {
        return metaCount;
      }
      return 0;
    },
  },
  async mounted () {
    await this.fetchArchiveCounts();

    if (this.isPastStatusAlias && !this.customJobsStatuses.length) {
      this.$router.replace({
        name: 'dashboard::pro::jobs',
        params: this.$route.params,
        query: {
          ...this.$route.query,
          [this.$options.JOBS_QUERY_PARAMS.STATUSES]: this.pastMatchStatuses,
        },
      });
    }

    global.addEventListener('click', this.onOutsideClick, true);
  },
  destroyed () {
    global.removeEventListener('click', this.onOutsideClick, true);
  },
  methods: {
    onFilterInput (selectedValues) {
      this.$router.push({
        name: 'dashboard::pro::jobs',
        params: this.$route.params,
        query: {
          ...this.$route.query,
          [this.$options.JOBS_QUERY_PARAMS.STATUSES]: selectedValues,
        },
      });
    },
    onFilterInteraction (isOpen = false) {
      if (isOpen) {
        this.isFilterOpened = isOpen;

        return;
      }

      // Need timeout only for closing.
      // Otherwise outside click event will be invoked after closing of filter dropdown.
      global.setTimeout(() => {
        this.isFilterOpened = isOpen;
      }, 100);
    },
    onOutsideClick (e) {
      if (!this.isFilterOpened || this.$refs.listbox.$el.contains(e.target)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      this.onFilterInteraction();
    },
    async fetchArchiveCounts () {
      try {
        const { data } = await http.get('/platform/workorder/r/workorder/prorders/v1', {
          params: {
            size: 0,
            with_aggregation: true,
            match_status: this.pastMatchStatuses.join(','),
          },
        }
        );
        this.updateCounts(data.aggregation.match_statuses);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    },
    updateCounts (counts) {
      this.statusAliases.forEach((statusAlias, index) => {
        this.statusAliases[index].count = counts[statusAlias.value] || 0;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  .TOP-NAVIGATION {
    @include padding-left(2, $md: 5);
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    padding-top: 1.5*$unit;
    padding-right: 2*$unit;
    padding-bottom: 1.5*$unit;
    border-bottom: 1px solid $color-gray30;
  }

  .FILTER {
    @include text-medium;
    color: $color-depot;

    :deep(.input) {
      height: auto;
      padding: 0;
      border: none;
      font-weight: 500;
      font-size: 14px;

      .VK-LISTBOX-TEXT {
        padding-right: $unit;
      }

      .CHEVRON {
        color: $color-depot;
      }
    }

    :deep(.MENU) {
      right: 0;
      height: auto;
      width: 186px;
      padding: $unit 0;
      border: none;
      box-shadow: 0 9px 10px 0 rgba(black, 0.2);
      overflow: auto;

      .OPTION {
        @include hover(transform, none, none);
        transition: none;

        &.SELECTED {
          background: none;
        }
      }
    }
  }
</style>
